<template>
  <v-list
    class="core-selection-list"
    :data-qa="listDataQa"
  >
    <template v-if="!preparedItems.length">
      <slot name="empty" />
    </template>

    <template v-else>
      <v-list-item
        v-for="({
          item, text, isSelected, highlightedText,
        }, index) in preparedItems"
        :key="index"
        :ref="isSelected ? 'selectedItemRef' : null"
        class="core-selection-list__item"
        :data-qa="listItemDataQa"
        @click="onItemClick(item)"
      >
        <slot
          name="item"
          :item="item"
          :text="text"
          :is-selected="isSelected"
          :highlighted-text="highlightedText"
        >
          <v-list-item-content
            class="d-block text-body-1 text--text"
            v-html="highlightedText/* eslint-disable-line vue/no-v-text-v-html-on-component vue/no-v-html */"
          />
        </slot>

        <slot
          name="end-icon"
          :item="item"
          :is-selected="isSelected"
        >
          <v-list-item-icon v-if="endIcon === 'check'">
            <v-icon
              v-if="isSelected"
              color="primary"
            >
              ui-icon-check
            </v-icon>
          </v-list-item-icon>
          <v-list-item-icon v-else-if="endIcon === 'arrow'">
            <v-icon color="icon-secondary">
              ui-icon-arrow-right
            </v-icon>
          </v-list-item-icon>
        </slot>
      </v-list-item>
    </template>
  </v-list>
</template>

<script setup lang="ts">
/**
 * Список для выбора одного или нескольких пунктов.
 *
 * @example
 * <CoreSelectionList v-model="city" :items="cities" />
 * <CoreSelectionList v-model="cities" multiple end-icon="check" :items="allCities" />
 *
 * Слоты: `item`, `end-icon`, `empty`.
 * Ref: `scrollToActiveItem()`.
 */
import {
  ref, watch, toRefs, computed, onMounted, nextTick,
} from 'vue'
import { debounce } from 'lodash'
import getTextValue from 'src/core/CoreSelectionList/functions/getTextValue'
import filterItemsBySearch from 'src/core/CoreSelectionList/functions/filterItemsBySearch'
import highlightSearchText from 'src/core/CoreSelectionList/functions/highlightSearchText'
import toggleSelectedItem from 'src/core/CoreSelectionList/functions/toggleSelectedItem'
import toSelectedItemsArray from 'src/core/CoreSelectionList/functions/toSelectedItemsArray'
import type {
  CoreSelectionListItem,
  CoreSelectionListEndIcon,
  CoreSelectionListSelectedItem,
} from 'src/core/CoreSelectionList/types'

/** Данные пункта + подпись для отрисовки. */
type PreparedItem = {
  item: CoreSelectionListItem
  text: string
  isSelected: boolean
  /** Подпись; при поиске совпадения в HTML. */
  highlightedText: string
}

type Emits = {
  (_event: 'input', _selectedItem: CoreSelectionListSelectedItem): void
}

const emit = defineEmits<Emits>()

type Props = {
  /** Пункты списка */
  items: CoreSelectionListItem[]
  /** Из какого поля объекта брать подпись, напр. `name` → `{ name: 'Казань' }` */
  itemText?: string
  /** Текущий выбор (`v-model`): объект или массив при `multiple` */
  value?: CoreSelectionListSelectedItem
  /** Разрешить выбрать несколько пунктов сразу */
  multiple?: boolean
  /**
   * Строка поиска: фильтрует список и подсвечивает совпадения.
   * Пока символов меньше filterMinLength — фильтр выключен.
   */
  searchInput?: string
  /** Минимум символов в поиске, чтобы начать фильтровать (по умолчанию 2) */
  filterMinLength?: number
  /** Пауза перед фильтром при вводе, мс (по умолчанию 250) */
  filterDebounceMs?: number
  /** Иконка справа: check | arrow | none */
  endIcon?: CoreSelectionListEndIcon
  /** data-qa корневого списка */
  listDataQa?: string
  /** data-qa пункта списка */
  listItemDataQa?: string
}

const props = withDefaults(defineProps<Props>(), {
  itemText: 'name',
  searchInput: '',
  value: undefined,
  multiple: false,
  filterMinLength: 2,
  filterDebounceMs: 250,
  endIcon: 'arrow',
  listDataQa: 'core_selection_list',
  listItemDataQa: 'core_selection_list_item',
})

const {
  items, value, searchInput, filterMinLength, multiple, itemText,
} = toRefs(props)

// Поиск с небольшой задержкой (чтобы не фильтровать на каждый символ)

const delayedSearchQuery = ref(searchInput.value)

const setDelayedSearchQuery = debounce((query: string) => {
  delayedSearchQuery.value = query
}, props.filterDebounceMs)

watch(searchInput, query => {
  if (query) {
    setDelayedSearchQuery(query)
  } else {
    // Очистку применяем сразу, без ожидания debounce
    delayedSearchQuery.value = ''
  }
})

/** Подписи выбранных пунктов — по ним быстро понимаем, выделен ли пункт в списке */
const selectedItemTexts = computed(() => new Set(
  toSelectedItemsArray(value.value).map(item => getTextValue(item, itemText.value)),
))

const preparedItems = computed((): PreparedItem[] => {
  const query = delayedSearchQuery.value
  const isSearchActive = query.trim().length >= filterMinLength.value

  // Либо все пункты, либо только подходящие под поиск
  const itemsToShow = isSearchActive
    ? filterItemsBySearch(items.value, query, itemText.value)
    : items.value

  return itemsToShow.map(item => {
    const text = getTextValue(item, itemText.value)

    return {
      item,
      text,
      isSelected: selectedItemTexts.value.has(text),
      highlightedText: isSearchActive ? highlightSearchText(text, query) : text,
    }
  })
})

function onItemClick(item: CoreSelectionListItem) {
  const toggledSelectedItems = toggleSelectedItem(
    toSelectedItemsArray(value.value),
    item,
    itemText.value,
  )
  const nextSelectedItem = multiple.value ? toggledSelectedItems : item

  emit('input', nextSelectedItem)
}

const selectedItemRef = ref<{ $el: HTMLElement }[] | null>(null)

/** Прокрутка к выбранному пункту (при multiple — к первому). */
async function scrollToActiveItem() {
  await nextTick()

  if (!selectedItemRef.value?.length) {
    return
  }

  selectedItemRef.value[0].$el.scrollIntoView({ block: 'center' })
}

onMounted(scrollToActiveItem)

defineExpose({
  scrollToActiveItem,
})
</script>

<style lang="scss" scoped>
.core-selection-list__item {
  min-height: 56px;
  padding: 0 16px;

  :deep(.v-list-item__icon) {
    align-self: center;
    margin-top: 0;
    margin-bottom: 0;
  }
}
</style>
